import CarouselSwiper, {
    NavigationControls,
    useCarouselSwiperController,
} from '@eventbrite/eds-carousel-swiper';
import { ProgressIndicator } from '@eventbrite/eds-progress-indicator';
import { FormattedEvent } from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { ArrowRightIcon, Typography } from '@eventbrite/marmalade';
import { logEvent } from '@eventbrite/statsig';
import React from 'react';
import blobCurve from '../../../src/static/images/WeekendBucket/blobCurve.png';
import { USER_WEEKEND_INPERSON_EVENTS } from '../../constants/constants';
import { DateFilter } from '../../constants/dates';
// import { FOR_YOU } from '../../constants/tabConfig';
import { WEEKEND_GUIDE_EVENT_CLICK } from '../../pages/home/constants/analytics';
import { useWindowWidth } from '../../utils/dom';
import { getLocationInfo } from '../../utils/venue';
import { DiscoverVerticalEventCard } from '../DiscoverEventCard/Vertical/DiscoverVerticalBrowseEventCard';
import './WeekendBucket.scss';

const cardWidth = 297;
const itemsToShow = 4;
/*const date = new Date();

const today = date.getDay();

function setDateFromToday(daysAfter: number) {
    const _date = new Date();
    const updatedDate = _date.setDate(_date.getDate() + daysAfter);
    return new Date(updatedDate).toISOString().split('T')[0];
}

const filtersByDays = {
    3: [
        { label: 'Friday', date: setDateFromToday(2), selected: false },
        { label: 'Saturday', date: setDateFromToday(3), selected: false },
        { label: 'Sunday', date: setDateFromToday(4), selected: false },
    ],
    4: [
        { label: 'Tomorrow', date: setDateFromToday(1), selected: false },
        { label: 'Saturday', date: setDateFromToday(2), selected: false },
        { label: 'Sunday', date: setDateFromToday(3), selected: false },
    ],
    5: [
        { label: 'Today', date: setDateFromToday(0), selected: false },
        { label: 'Tomorrow', date: setDateFromToday(1), selected: false },
        { label: 'Sunday', date: setDateFromToday(2), selected: false },
    ],
};

const getInitFiltersByDay =
    filtersByDays[today as keyof typeof filtersByDays]?.map((filter) => ({
        ...filter,
        selected: filter.date === date.toISOString().split('T')[0],
    })) ?? [];*/

interface WeekendBucketProps {
    events: Array<FormattedEvent>;
    locale: string;
    firstName: string;
    dateFilter: DateFilter;
    filterSelected: Function;
    isLoading: boolean;
    locationSlug: string;
}

export const WeekendBucket: React.FunctionComponent<WeekendBucketProps> = ({
    events,
    locale,
    firstName,
    //dateFilter,
    //filterSelected,
    isLoading,
    locationSlug,
}) => {
    const windowWidth = useWindowWidth();
    const isMobile = (windowWidth || 0) <= 480;
    const viewAllEventsUrl = `/d/${locationSlug}/events--this-weekend/`;

    const items = events?.map((event) => {
        const formattedEvent = {
            ...event,
            locationInfo:
                getLocationInfo(
                    event.venue?.name,
                    event.venue?.address?.localizedAreaDisplay,
                )?.toString() || '',
        };
        return (
            <DiscoverVerticalEventCard
                key={event.id}
                event={formattedEvent}
                locale={locale}
                isAuthenticated={true}
                affCode={USER_WEEKEND_INPERSON_EVENTS}
                onClick={() => logEvent(WEEKEND_GUIDE_EVENT_CLICK)}
            />
        );
    });

    const {
        isLastSlide,
        nextSlide,
        prevSlide,
        currentIndex,
        isFirstSlide,
        setCurrentIndex,
        isEndOfCarousel,
    } = useCarouselSwiperController({
        initialSlide: 0,
        items: items || [],
        stopAtCarouselEnd: true,
        itemsToShow: itemsToShow,
    });
    /*
    const [filters, setFilters] = useState(getInitFiltersByDay);

    const handleDateFilterChange = (date: string) => {
        dateFilter.handleCustomDateSelect?.({
            startDate: date,
            endDate: date,
            focusedTab: FOR_YOU,
        });

        const updatedFilters = filters.map((filter) => ({
            ...filter,
            selected: filter.date === date ? !filter.selected : false,
        }));

        setFilters(updatedFilters);
        setCurrentIndex(0);
        filterSelected(true);
    };*/

    return (
        <div className="weekend-bucket">
            {isLoading ? (
                <div className="weekend-bucket__search-loading">
                    <ProgressIndicator style="light" shape="circular" />
                </div>
            ) : null}
            <Typography>
                <div className="weekend-bucket_header">
                    <span className="weekend-bucket_header-pink">
                        {gettext('Your ')}
                    </span>
                    {gettext('weekend guide')} 🎉
                </div>
                <div className="weekend-bucket_subheader">
                    {gettext(
                        '%(firstName)s, make the perfect plans with these recommendations',
                        { firstName },
                    )}
                </div>
            </Typography>
            {/* temporary hide for experiment
            <div className="weekend-bucket__date-filters">
                {filters.map((filter) => (
                    <div
                        key={filter.date}
                        onClick={() => {
                            handleDateFilterChange(filter.date);
                        }}
                    >
                        <Pill
                            className={
                                filter.selected
                                    ? 'weekend-bucket__pill-clicked'
                                    : 'weekend-bucket__pill'
                            }
                        >
                            {gettext('%(label)s', { label: filter.label })}
                        </Pill>
                    </div>
                ))}
            </div>*/}
            {!isMobile ? (
                <div className="weekend-bucket__view-all">
                    <a href={viewAllEventsUrl}>{gettext('View all events ')}</a>
                </div>
            ) : null}
            <div className="weekend-bucket_mobile-curved-bottom">
                <img
                    className="weekend-bucket_mobile-curved-bottom-image"
                    src={blobCurve}
                />
                <div className="weekend-bucket_subheader">
                    {gettext(
                        '%(firstName)s, make the perfect plans with these recommendations',
                        {
                            firstName,
                        },
                    )}
                </div>
            </div>
            {!isLoading && !items.length ? (
                <div className="weekend-bucket__empty-state">
                    <h2>There are no events for the selected filter</h2>
                    <p>Please try a different filter</p>
                </div>
            ) : null}
            <div className="weekend-bucket__carousel">
                <CarouselSwiper
                    useBottomProgressBar={true}
                    className="weekend-bucket__carousel__swiper"
                    controls={
                        !isMobile ? (
                            <NavigationControls
                                className="weekend-bucket__carousel__controls"
                                isFirstSlide={isFirstSlide}
                                isLastSlide={isLastSlide}
                                nextSlide={nextSlide}
                                prevSlide={prevSlide}
                                setCurrentIndex={setCurrentIndex}
                                showPrevSlideBtn={
                                    !isFirstSlide && items.length > itemsToShow
                                }
                                showNextSlideBtn={
                                    !isEndOfCarousel &&
                                    items.length > itemsToShow
                                }
                            />
                        ) : null
                    }
                    currentIndex={currentIndex}
                    enableNavigationLooping
                    items={items}
                    itemsToShow={itemsToShow}
                    setCurrentIndex={setCurrentIndex}
                    animateSlides={true}
                    hidePreviousSlide={true}
                    hideProgressBars={!isMobile}
                    disableNextSlides={false}
                    customTranslate={`-${currentIndex * cardWidth}px`}
                    ignoreTransitionOnEventClick={true}
                />
            </div>
            {isMobile ? (
                <div className="weekend-bucket__view-all">
                    <a href={viewAllEventsUrl}>
                        {gettext('View all events ')}
                        <span className="weekend-bucket__view-all-arrow">
                            <ArrowRightIcon />
                        </span>
                    </a>
                </div>
            ) : null}
        </div>
    );
};
